/**
 * WineDepo quiz scoring engine v1.6
 * Source of truth: docs/quiz-v1.6/.../scoring-spec.md + quiz-config.v1.6.json
 * No Shopify tags. Scores from quiz.* metafields only.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.WdQuizV12 = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function quizLocale() {
    var root = typeof document !== 'undefined' && document.querySelector('[data-quiz-v12]');
    var fromRoot = root && root.getAttribute('data-locale');
    var raw =
      fromRoot ||
      (typeof Shopify !== 'undefined' && (Shopify.locale || Shopify.legacyLocale)) ||
      (typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) ||
      'ru';
    return String(raw).toLowerCase().indexOf('uz') === 0 ? 'uz' : 'ru';
  }

  function qi(ru, uz) {
    return quizLocale() === 'uz' ? uz : ru;
  }

  var DIMENSIONS = ['bubbles', 'freshness', 'sweetness', 'body', 'grip'];

  var ALLOWED_COLOURS = { white: 1, rose: 1, red: 1, orange: 1 };

  var ALLOWED_STYLE_FAMILIES = {
    sparkling_crisp: 1,
    sparkling_round: 1,
    crisp_white: 1,
    aromatic_white: 1,
    round_white: 1,
    dry_rose: 1,
    fruit_rose: 1,
    light_red: 1,
    structured_red: 1,
    rich_red: 1,
    sweet_wine: 1,
    orange_wine: 1
  };

  function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
  }

  function isIntInRange(n, lo, hi) {
    return typeof n === 'number' && Number.isInteger(n) && n >= lo && n <= hi;
  }

  function toIntScore(value) {
    if (value == null || value === '') return null;
    var n = typeof value === 'number' ? value : parseInt(String(value), 10);
    return Number.isInteger(n) ? n : null;
  }

  function normalizeAromaTags(tags) {
    if (Array.isArray(tags)) return tags;
    if (typeof tags !== 'string') return null;
    var raw = tags.trim();
    if (!raw) return null;
    if (raw.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
      } catch (e) {
        return null;
      }
    }
    return raw
      .split(/[,;|]/)
      .map(function (part) {
        return String(part).trim();
      })
      .filter(Boolean);
  }

  function normalizeWine(wine) {
    if (!wine) return wine;
    var w = Object.assign({}, wine);
    var scoreKeys = [
      'bubbles_score',
      'freshness_score',
      'sweetness_score',
      'body_score',
      'grip_score'
    ];
    for (var i = 0; i < scoreKeys.length; i++) {
      var key = scoreKeys[i];
      if (w[key] != null) w[key] = toIntScore(w[key]);
    }
    if (w.recommendable === 'true') w.recommendable = true;
    if (w.recommendable === 'false') w.recommendable = false;
    if (typeof w.profile_status === 'string') w.profile_status = w.profile_status.trim();
    if (typeof w.profile_version === 'string') w.profile_version = w.profile_version.trim();
    if (typeof w.colour === 'string') w.colour = w.colour.trim().toLowerCase();
    if (typeof w.style_family === 'string') w.style_family = w.style_family.trim();
    if (typeof w.tasting_summary === 'string') w.tasting_summary = w.tasting_summary.trim();
    var tags = normalizeAromaTags(w.aroma_tags);
    if (tags) w.aroma_tags = tags;
    return w;
  }

  function questionById(config, id) {
    for (var i = 0; i < config.questions.length; i++) {
      if (config.questions[i].id === id) return config.questions[i];
    }
    return null;
  }

  function answerById(question, answerId) {
    if (!question || !question.answers) return null;
    for (var i = 0; i < question.answers.length; i++) {
      if (question.answers[i].id === answerId) return question.answers[i];
    }
    return null;
  }

  function emptyDimMap(fill) {
    var o = {};
    for (var i = 0; i < DIMENSIONS.length; i++) o[DIMENSIONS[i]] = fill;
    return o;
  }

  /**
   * @param {object} config quiz-config.v1.6.json
   * @param {Record<string, string|null>} answers map questionId -> answerId | 'skip' | null
   */
  function buildProfile(config, answers) {
    var profile = emptyDimMap(50);
    var confidence = emptyDimMap(0);
    var weightedSignal = emptyDimMap(0);
    var information = emptyDimMap(0);
    var userAroma = {};
    var colourRoute = 'any';
    var meaningfulAnswers = 0;
    var gripEvidenceQuestions = [];
    var contributingAnswers = [];
    var directAnswerIds = { bubbles: null, sweetness: null };

    var qList = config.questions || [];
    for (var qi = 0; qi < qList.length; qi++) {
      var q = qList[qi];
      var raw = answers[q.id];
      if (raw == null || raw === '' || raw === 'skip') continue;

      var ans = answerById(q, raw);
      if (!ans) continue;

      var contributed = false;

      if (q.kind === 'direct_dimension') {
        profile[q.dimension] = ans.directValue;
        confidence[q.dimension] = ans.confidence;
        if (q.dimension === 'bubbles' || q.dimension === 'sweetness') {
          directAnswerIds[q.dimension] = ans.id;
        }
        if (ans.confidence > 0) contributed = true;
        contributingAnswers.push({
          questionId: q.id,
          answerId: ans.id,
          label: ans.label,
          kind: 'direct',
          dimension: q.dimension,
          confidence: ans.confidence
        });
      } else if (q.kind === 'direct_colour') {
        colourRoute = ans.colourRoute || 'any';
        contributed = true;
        contributingAnswers.push({
          questionId: q.id,
          answerId: ans.id,
          label: ans.label,
          kind: 'colour',
          colourRoute: colourRoute
        });
      }

      var r = typeof q.reliability === 'number' ? q.reliability : 0;
      var signals = ans.signals || {};
      var sigKeys = Object.keys(signals);
      var hasNumericSignal = false;
      var hasGripSignal = false;

      for (var si = 0; si < sigKeys.length; si++) {
        var d = sigKeys[si];
        var a = signals[d];
        if (typeof a !== 'number') continue;
        hasNumericSignal = true;
        weightedSignal[d] = (weightedSignal[d] || 0) + r * a;
        information[d] = (information[d] || 0) + r * Math.abs(a);
        if (d === 'grip' && Math.abs(a) > 0) hasGripSignal = true;
      }

      if (hasGripSignal && gripEvidenceQuestions.indexOf(q.id) === -1) {
        gripEvidenceQuestions.push(q.id);
      }

      var aromaTags = ans.aromaTags || {};
      var aromaKeys = Object.keys(aromaTags);
      var hasAroma = false;
      for (var ai = 0; ai < aromaKeys.length; ai++) {
        var tag = aromaKeys[ai];
        var w = aromaTags[tag];
        if (typeof w !== 'number' || w === 0) continue;
        hasAroma = true;
        userAroma[tag] = (userAroma[tag] || 0) + r * w;
      }

      if (hasNumericSignal || hasAroma) {
        contributed = true;
        contributingAnswers.push({
          questionId: q.id,
          answerId: ans.id,
          label: ans.label,
          kind: 'everyday',
          signals: signals,
          aromaTags: aromaTags
        });
      }

      if (contributed) meaningfulAnswers += 1;
    }

    // Everyday directAdjustments (e.g. lemon_choy sweetness +6) after direct profile is set
    for (var daj = 0; daj < contributingAnswers.length; daj++) {
      var ca = contributingAnswers[daj];
      if (ca.kind !== 'everyday') continue;
      var qAdj = questionById(config, ca.questionId);
      var aAdj = answerById(qAdj, ca.answerId);
      if (!aAdj || !aAdj.directAdjustments) continue;
      var adjKeys = Object.keys(aAdj.directAdjustments);
      for (var aki = 0; aki < adjKeys.length; aki++) {
        var adjDim = adjKeys[aki];
        var adjVal = aAdj.directAdjustments[adjDim];
        if (typeof adjVal !== 'number') continue;
        profile[adjDim] = clamp((profile[adjDim] || 50) + adjVal, 0, 100);
      }
    }

    var dims = config.dimensions || {};
    for (var di = 0; di < DIMENSIONS.length; di++) {
      var dim = DIMENSIONS[di];
      var meta = dims[dim];
      if (!meta || meta.source !== 'inferred') continue;

      var info = information[dim] || 0;
      var target = meta.evidenceTarget || 1;
      if (info === 0) {
        profile[dim] = 50;
        confidence[dim] = 0;
      } else {
        var signedMean = clamp(weightedSignal[dim] / info, -1, 1);
        var conf = Math.min(1, info / target);
        confidence[dim] = conf;
        profile[dim] = 50 + 50 * signedMean * conf;
      }
    }

    var weightSum = 0;
    var confWeighted = 0;
    for (var wi = 0; wi < DIMENSIONS.length; wi++) {
      var dw = DIMENSIONS[wi];
      var wgt = (dims[dw] && dims[dw].weight) || 1;
      weightSum += wgt;
      confWeighted += wgt * (confidence[dw] || 0);
    }
    var overallConfidence = weightSum > 0 ? confWeighted / weightSum : 0;

    var minMeaningful = (config.ranking && config.ranking.minimumMeaningfulAnswers) || 5;
    var remaining = Math.max(0, minMeaningful - meaningfulAnswers);

    return {
      profile: profile,
      confidence: confidence,
      colourRoute: colourRoute,
      meaningfulAnswers: meaningfulAnswers,
      remainingMeaningfulNeeded: remaining,
      overallConfidence: overallConfidence,
      gripEvidenceQuestions: gripEvidenceQuestions.slice(),
      gripEvidenceCount: gripEvidenceQuestions.length,
      userAroma: userAroma,
      directAnswerIds: directAnswerIds,
      contributingAnswers: contributingAnswers,
      readyForCards: remaining === 0,
      showPercent:
        remaining === 0 &&
        overallConfidence >=
          ((config.ranking && config.ranking.minimumOverallConfidenceForPercent) || 0.65)
    };
  }

  function topUserAromaTags(userAroma, aromaRanking) {
    var minW = (aromaRanking && aromaRanking.minimumUserTagWeight) || 0.4;
    var order = (aromaRanking && aromaRanking.tieBreakOrder) || [];
    var exclude = (aromaRanking && aromaRanking.excludeFromStandardBonus) || [];
    var tags = Object.keys(userAroma).filter(function (t) {
      if (exclude.indexOf(t) !== -1) return false;
      return userAroma[t] >= minW;
    });
    tags.sort(function (a, b) {
      var diff = userAroma[b] - userAroma[a];
      if (diff !== 0) return diff;
      return order.indexOf(a) - order.indexOf(b);
    });
    return tags.slice(0, 2);
  }

  function aromaBonusForWine(wineTags, topTags, aromaRanking) {
    if (!topTags.length) return 0;
    var set = {};
    for (var i = 0; i < (wineTags || []).length; i++) set[wineTags[i]] = 1;
    var bonus = 0;
    if (topTags[0] && set[topTags[0]]) bonus += aromaRanking.firstTagBonus || 2;
    if (topTags[1] && set[topTags[1]]) bonus += aromaRanking.secondTagBonus || 1;
    return Math.min(bonus, aromaRanking.maximumBonus || 3);
  }

  function validateWine(wine, config) {
    var reasons = [];
    var cv = config.catalogueValidation || {};
    var allowedAroma = {};
    (config.aromaTags || []).forEach(function (t) {
      allowedAroma[t] = 1;
    });

    if (!wine.published) reasons.push('not_published');
    if (!wine.availableForSale) reasons.push('not_available');
    if (cv.requireRecommendable !== false && wine.recommendable !== true) {
      reasons.push('not_recommendable');
    }
    if (cv.requireApprovedStatus !== false && wine.profile_status !== 'approved') {
      reasons.push('profile_status_not_approved');
    }
    if (cv.requireCurrentProfileVersion !== false && wine.profile_version !== config.version) {
      reasons.push('profile_version_mismatch');
    }

    var lo = cv.scoreMinimum != null ? cv.scoreMinimum : 0;
    var hi = cv.scoreMaximum != null ? cv.scoreMaximum : 100;
    var scoreKeys = [
      'bubbles_score',
      'freshness_score',
      'sweetness_score',
      'body_score',
      'grip_score'
    ];
    for (var i = 0; i < scoreKeys.length; i++) {
      var k = scoreKeys[i];
      if (wine[k] == null) reasons.push('missing_' + k);
      else if (!isIntInRange(wine[k], lo, hi)) reasons.push('invalid_' + k);
    }

    if (!ALLOWED_COLOURS[wine.colour]) reasons.push('invalid_colour');
    if (!ALLOWED_STYLE_FAMILIES[wine.style_family]) reasons.push('invalid_style_family');

    var tags = wine.aroma_tags;
    if (!Array.isArray(tags)) {
      reasons.push('invalid_aroma_tags');
    } else {
      var uniq = {};
      var bad = false;
      for (var ti = 0; ti < tags.length; ti++) {
        if (!allowedAroma[tags[ti]]) bad = true;
        if (uniq[tags[ti]]) bad = true;
        uniq[tags[ti]] = 1;
      }
      var minT = cv.minimumAromaTags != null ? cv.minimumAromaTags : 1;
      var maxT = cv.maximumAromaTags != null ? cv.maximumAromaTags : 3;
      if (tags.length < minT || tags.length > maxT || bad) {
        reasons.push('invalid_aroma_tags');
      }
    }

    if (!wine.tasting_summary || String(wine.tasting_summary).trim() === '') {
      reasons.push('empty_tasting_summary');
    }

    return { ok: reasons.length === 0, reasons: reasons };
  }

  /**
   * seek + red is a combined route: still reds must survive the general
   * seek (bubbles >= 55) hard filter and be assembled later in formResults.
   */
  function isSeekRedCombined(profileState) {
    var bubblesAns = profileState && profileState.directAnswerIds && profileState.directAnswerIds.bubbles;
    var colourRoute = (profileState && profileState.colourRoute) || 'any';
    return bubblesAns === 'seek' && colourRoute === 'red';
  }

  function isLikeRedCombined(profileState) {
    var bubblesAns = profileState && profileState.directAnswerIds && profileState.directAnswerIds.bubbles;
    var colourRoute = (profileState && profileState.colourRoute) || 'any';
    return bubblesAns === 'like' && colourRoute === 'red';
  }

  function hardFilterReasons(wine, profileState, config) {
    var reasons = [];
    var hf = config.hardFilters || {};
    var bubblesAns = profileState.directAnswerIds.bubbles;
    var sweetnessAns = profileState.directAnswerIds.sweetness;
    var seekRed = isSeekRedCombined(profileState);

    // Colour is NOT a hard filter in v1.6 — exact colour gets a ranking bonus instead.
    // Combined like+red / seek+red routes restrict the pool after scoring.
    // IMPORTANT: seek+red must be decided BEFORE the general seek sparkling-only filter,
    // otherwise still reds are wiped and the combined route gets an empty pool.

    if (bubblesAns && hf.bubblesByAnswer && hf.bubblesByAnswer[bubblesAns]) {
      var br = hf.bubblesByAnswer[bubblesAns];
      if (br.excludeAtOrAbove != null && wine.bubbles_score >= br.excludeAtOrAbove) {
        reasons.push('hard_filter_bubbles');
      }
      if (br.excludeAtOrBelow != null && wine.bubbles_score <= br.excludeAtOrBelow) {
        reasons.push('hard_filter_bubbles');
      }
      if (br.includeAtOrAbove != null && wine.bubbles_score < br.includeAtOrAbove) {
        // Skip sparkling-only seek filter for seek+red (still reds stay candidates).
        if (!seekRed) {
          reasons.push('hard_filter_bubbles');
        }
      }
    }

    if (sweetnessAns && hf.sweetnessByAnswer && hf.sweetnessByAnswer[sweetnessAns]) {
      var sr = hf.sweetnessByAnswer[sweetnessAns];
      if (sr.excludeAtOrAbove != null && wine.sweetness_score >= sr.excludeAtOrAbove) {
        reasons.push('hard_filter_sweetness');
      }
      if (sr.excludeAtOrBelow != null && wine.sweetness_score <= sr.excludeAtOrBelow) {
        reasons.push('hard_filter_sweetness');
      }
    }

    var gripRule = hf.gripByProfile || {};
    var minEv = gripRule.minimumEvidenceQuestions != null ? gripRule.minimumEvidenceQuestions : 2;
    var minConf = gripRule.minimumConfidence != null ? gripRule.minimumConfidence : 0.55;
    if (
      profileState.gripEvidenceCount >= minEv &&
      (profileState.confidence.grip || 0) >= minConf
    ) {
      var g = profileState.profile.grip;
      if (
        g <= (gripRule.lowProfileAtOrBelow != null ? gripRule.lowProfileAtOrBelow : 25) &&
        wine.grip_score >=
          (gripRule.excludeWineAtOrAboveForLowProfile != null
            ? gripRule.excludeWineAtOrAboveForLowProfile
            : 70)
      ) {
        reasons.push('hard_filter_grip');
      }
      if (
        g >= (gripRule.highProfileAtOrAbove != null ? gripRule.highProfileAtOrAbove : 75) &&
        wine.grip_score <=
          (gripRule.excludeWineAtOrBelowForHighProfile != null
            ? gripRule.excludeWineAtOrBelowForHighProfile
            : 15)
      ) {
        reasons.push('hard_filter_grip');
      }
    }

    return reasons;
  }

  function baseMatch(profileState, wine, config) {
    var dims = config.dimensions || {};
    var num = 0;
    var den = 0;
    for (var i = 0; i < DIMENSIONS.length; i++) {
      var d = DIMENSIONS[i];
      var w = (dims[d] && dims[d].weight) || 1;
      var c = profileState.confidence[d] || 0;
      if (c <= 0) continue;
      var wineScore = wine[d + '_score'];
      var diff = profileState.profile[d] - wineScore;
      num += w * c * diff * diff;
      den += w * c;
    }
    if (den === 0) return null;
    var distance = Math.sqrt(num / den);
    return 100 - distance;
  }

  function spiceOakAdjustment(wine, userAroma, aromaRanking) {
    var rules =
      aromaRanking &&
      aromaRanking.signedTagAdjustments &&
      aromaRanking.signedTagAdjustments.spice_oak;
    if (!rules) return 0;
    var tags = wine.aroma_tags || [];
    if (tags.indexOf('spice_oak') === -1) return 0;
    var u = userAroma && typeof userAroma.spice_oak === 'number' ? userAroma.spice_oak : 0;
    if (u <= (rules.dislikeAtOrBelow != null ? rules.dislikeAtOrBelow : -0.4)) {
      return rules.dislikeAdjustment != null ? rules.dislikeAdjustment : -2;
    }
    var softMin = rules.softLikeMinimum != null ? rules.softLikeMinimum : 0.2;
    var softMax = rules.softLikeMaximumExclusive != null ? rules.softLikeMaximumExclusive : 0.4;
    if (u >= softMin && u < softMax) {
      return rules.softLikeAdjustment != null ? rules.softLikeAdjustment : 1;
    }
    if (u >= (rules.strongLikeAtOrAbove != null ? rules.strongLikeAtOrAbove : 0.4)) {
      return rules.strongLikeAdjustment != null ? rules.strongLikeAdjustment : 2;
    }
    return 0;
  }

  function presentationScoreForWine(wine, config) {
    var sp = (config && config.scorePresentation && config.scorePresentation.presentationScore) || {};
    var boost = sp.priorityTierBoost != null ? sp.priorityTierBoost : 101;
    if (wine && wine.prioritySparklingTier) {
      return (wine.finalMatch || 0) + boost;
    }
    return wine && wine.finalMatch != null ? wine.finalMatch : 0;
  }

  /**
   * Normalize presentationScore values of the final selected set into displayMatch 87–99.
   * Mutates each item with presentationScore + displayMatch. Never used for ranking/filters.
   */
  function normalizeDisplayMatches(selected, config) {
    var list = selected || [];
    var f =
      (config && config.scorePresentation && config.scorePresentation.displayMatchFormula) || {};
    var displayMin = f.displayMinimum != null ? f.displayMinimum : 87;
    var displayMax = f.displayMaximum != null ? f.displayMaximum : 99;
    var singleVal = f.singleResultValue != null ? f.singleResultValue : 99;
    var equalVal = f.equalRangeValue != null ? f.equalRangeValue : 93;
    var span = displayMax - displayMin;

    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (item.presentationScore == null) {
        item.presentationScore = presentationScoreForWine(item, config);
      }
    }

    if (!list.length) return list;

    if (list.length === 1) {
      list[0].displayMatch = singleVal;
      return list;
    }

    var minScore = list[0].presentationScore;
    var maxScore = list[0].presentationScore;
    for (var j = 1; j < list.length; j++) {
      var ps = list[j].presentationScore;
      if (ps < minScore) minScore = ps;
      if (ps > maxScore) maxScore = ps;
    }

    if (maxScore === minScore) {
      for (var k = 0; k < list.length; k++) list[k].displayMatch = equalVal;
      return list;
    }

    for (var n = 0; n < list.length; n++) {
      list[n].displayMatch = Math.round(
        displayMin + span * (list[n].presentationScore - minScore) / (maxScore - minScore)
      );
    }
    return list;
  }

  /** @deprecated Use normalizeDisplayMatches on the final selected set. Single-card case → 99. */
  function toDisplayMatch(finalMatch, config) {
    var probe = { finalMatch: finalMatch, presentationScore: finalMatch };
    normalizeDisplayMatches([probe], config);
    return probe.displayMatch;
  }

  function colourBonusForWine(wine, profileState, config) {
    var ranking = (config && config.ranking) || {};
    var pref = ranking.colourPreference || {};
    if (pref.allowAlternativeColours === false) return 0;
    var route = (profileState && profileState.colourRoute) || 'any';
    if (!route || route === 'any') return 0;
    if (wine.colour !== route) return 0;
    return pref.exactColourBonus != null ? pref.exactColourBonus : 5;
  }

  function scoreWine(wine, profileState, topTags, config) {
    var bm = baseMatch(profileState, wine, config);
    if (bm == null) return null;
    var aromaRanking = config.aromaRanking || {};
    var bonus = aromaBonusForWine(wine.aroma_tags, topTags, aromaRanking);
    var spiceAdj = spiceOakAdjustment(wine, profileState.userAroma || {}, aromaRanking);
    var colourBonus = colourBonusForWine(wine, profileState, config);
    var finalMatch = clamp(
      Math.round(bm + bonus + spiceAdj + colourBonus),
      0,
      (config.ranking && config.ranking.maximumScore) || 100
    );
    return {
      handle: wine.handle,
      title: wine.title,
      colour: wine.colour,
      style_family: wine.style_family,
      aroma_tags: wine.aroma_tags,
      tasting_summary: wine.tasting_summary,
      image: wine.image || null,
      url: wine.url || null,
      price: wine.price != null ? wine.price : null,
      bubbles_score: wine.bubbles_score,
      freshness_score: wine.freshness_score,
      sweetness_score: wine.sweetness_score,
      body_score: wine.body_score,
      grip_score: wine.grip_score,
      baseMatch: bm,
      aromaBonus: bonus,
      spiceAdjustment: spiceAdj,
      colourBonus: colourBonus,
      finalMatch: finalMatch
    };
  }

  function sortScored(a, b) {
    if (b.finalMatch !== a.finalMatch) return b.finalMatch - a.finalMatch;
    return String(a.handle).localeCompare(String(b.handle));
  }

  function countStyle(list, family) {
    var n = 0;
    for (var i = 0; i < list.length; i++) {
      if (list[i].style_family === family) n++;
    }
    return n;
  }

  function findStyleAlternative(candidate, remaining, selected, sacrifice, maxPerStyle) {
    if (countStyle(selected, candidate.style_family) < maxPerStyle) return candidate;
    for (var i = 0; i < remaining.length; i++) {
      var alt = remaining[i];
      if (alt.handle === candidate.handle) continue;
      if (selected.some(function (s) { return s.handle === alt.handle; })) continue;
      if (alt.style_family === candidate.style_family) continue;
      if (countStyle(selected, alt.style_family) >= maxPerStyle) continue;
      if (alt.finalMatch >= candidate.finalMatch - sacrifice) return alt;
    }
    return null;
  }

  function pickWithStyleGuard(candidate, remaining, selected, ranking) {
    var maxPerStyle = ranking.maxPerStyleFamily != null ? ranking.maxPerStyleFamily : 2;
    var sacrifice = ranking.maximumScoreSacrifice != null ? ranking.maximumScoreSacrifice : 5;
    var allowRepeat = ranking.allowStyleRepeatWhenNoCandidateWithinSacrifice !== false;

    if (countStyle(selected, candidate.style_family) < maxPerStyle) return candidate;

    var alt = findStyleAlternative(candidate, remaining, selected, sacrifice, maxPerStyle);
    if (alt) return alt;
    if (allowRepeat) return candidate;
    return null;
  }

  function formResults(scored, profileState, config) {
    var ranking = config.ranking || {};
    var minMatch = (config.hardFilters && config.hardFilters.minimumMatch) || 55;
    var eligible = scored
      .filter(function (s) {
        return s.finalMatch >= minMatch;
      })
      .sort(sortScored);

    var primaryCount = ranking.primaryResultCount != null ? ranking.primaryResultCount : 3;
    var maxCount = ranking.maximumResultCount != null ? ranking.maximumResultCount : 10;
    var selected = [];

    var bubblesAns = profileState.directAnswerIds && profileState.directAnswerIds.bubbles;
    var colourRoute = (profileState && profileState.colourRoute) || 'any';
    var combined = ranking.combinedRoutes || {};
    var redRoutes = combined.red || {};
    var likeRedRoute = isLikeRedCombined(profileState)
      ? redRoutes.like || {
          allowedStillColours: ['red'],
          allowedSparklingColours: ['red', 'rose'],
          minimumSparklingIfEligible: 1,
          maximumSparklingInResults: 4,
          forceSparklingToFirstPosition: false,
          skipSparklingRequirementWhenNoneEligible: true
        }
      : null;
    var seekRedRoute = isSeekRedCombined(profileState)
      ? redRoutes.seek || {
          allowedStillColours: ['red'],
          allowedSparklingColours: ['red', 'rose'],
          sparklingColourTiers: [['red'], ['rose']],
          useFirstNonEmptySparklingTierOnly: true,
          placeSparklingTierBeforeStill: true,
          allowStillAfterSparklingTier: true,
          excludeAllOtherColours: true
        }
      : null;

    var likeComp =
      bubblesAns === 'like' &&
      ranking.bubblesCompositionByAnswer &&
      ranking.bubblesCompositionByAnswer.like
        ? ranking.bubblesCompositionByAnswer.like
        : null;
    if (likeRedRoute) {
      likeComp = {
        sparklingAtOrAbove:
          (ranking.bubblesCompositionByAnswer &&
            ranking.bubblesCompositionByAnswer.like &&
            ranking.bubblesCompositionByAnswer.like.sparklingAtOrAbove) != null
            ? ranking.bubblesCompositionByAnswer.like.sparklingAtOrAbove
            : 55,
        minimumSparklingIfEligible:
          likeRedRoute.minimumSparklingIfEligible != null
            ? likeRedRoute.minimumSparklingIfEligible
            : 1,
        maximumSparklingInResults:
          likeRedRoute.maximumSparklingInResults != null
            ? likeRedRoute.maximumSparklingInResults
            : 4,
        minimumStillIfEligible:
          likeRedRoute.minimumStillIfEligible != null ? likeRedRoute.minimumStillIfEligible : 1
      };
    }

    var sparklingAt =
      likeComp && likeComp.sparklingAtOrAbove != null
        ? likeComp.sparklingAtOrAbove
        : ranking.bubblesCompositionByAnswer &&
            ranking.bubblesCompositionByAnswer.seek &&
            ranking.bubblesCompositionByAnswer.seek.sparklingAtOrAbove != null
          ? ranking.bubblesCompositionByAnswer.seek.sparklingAtOrAbove
          : 55;
    var maxSparkling =
      likeComp && likeComp.maximumSparklingInResults != null
        ? likeComp.maximumSparklingInResults
        : 4;
    var minStill =
      likeComp && likeComp.minimumStillIfEligible != null
        ? likeComp.minimumStillIfEligible
        : 1;
    var minSparkling =
      likeComp && likeComp.minimumSparklingIfEligible != null
        ? likeComp.minimumSparklingIfEligible
        : 1;

    function isSparkling(w) {
      return (w.bubbles_score || 0) >= sparklingAt;
    }

    function countSparkling(list) {
      var n = 0;
      for (var i = 0; i < list.length; i++) if (isSparkling(list[i])) n++;
      return n;
    }

    function countStill(list) {
      return list.length - countSparkling(list);
    }

    function inColourList(colour, list) {
      return list && list.indexOf(colour) !== -1;
    }

    function allowedInLikeRedPool(w) {
      if (!likeRedRoute) return true;
      if (isSparkling(w)) {
        return inColourList(w.colour, likeRedRoute.allowedSparklingColours || ['red', 'rose']);
      }
      return inColourList(w.colour, likeRedRoute.allowedStillColours || ['red']);
    }

    function allowedInSeekRedPool(w) {
      if (!seekRedRoute) return true;
      if (isSparkling(w)) {
        return inColourList(w.colour, seekRedRoute.allowedSparklingColours || ['red', 'rose']);
      }
      return inColourList(w.colour, seekRedRoute.allowedStillColours || ['red']);
    }

    if (likeRedRoute) {
      eligible = eligible.filter(allowedInLikeRedPool).sort(sortScored);
    } else if (seekRedRoute) {
      eligible = eligible.filter(allowedInSeekRedPool).sort(sortScored);
    }

    function remainingPool() {
      return eligible.filter(function (e) {
        return !selected.some(function (s) {
          return s.handle === e.handle;
        });
      });
    }

    function sparklingColourPriority() {
      var pref = ranking.colourPreference || {};
      var map = pref.sparklingFallbackPriorityBySelectedColour || {};
      var route = colourRoute || 'any';
      if (likeRedRoute) {
        return (likeRedRoute.allowedSparklingColours || map.red || ['red', 'rose']).slice();
      }
      if (map[route] && map[route].length) return map[route].slice();
      if (route && route !== 'any') return [route, 'rose', 'white', 'orange', 'red'];
      return [];
    }

    function pickPreferredSparkling(pool) {
      var sparks = pool.filter(isSparkling).sort(sortScored);
      if (!sparks.length) return null;
      var order = sparklingColourPriority();
      if (!order.length) return sparks[0];
      for (var ci = 0; ci < order.length; ci++) {
        var col = order[ci];
        for (var si = 0; si < sparks.length; si++) {
          if (sparks[si].colour === col) return sparks[si];
        }
      }
      // like+red: never fall back to disallowed colours (white/orange)
      if (likeRedRoute) return null;
      return sparks[0];
    }

    function tryAdd(wine, opts) {
      if (!wine) return false;
      opts = opts || {};
      if (
        selected.some(function (s) {
          return s.handle === wine.handle;
        })
      ) {
        return false;
      }
      if (likeComp && isSparkling(wine) && countSparkling(selected) >= maxSparkling) {
        return false;
      }
      var pool = remainingPool();
      var picked = opts.skipStyleGuard
        ? wine
        : pickWithStyleGuard(wine, pool, selected, ranking);
      if (!picked) return false;
      if (
        selected.some(function (s) {
          return s.handle === picked.handle;
        })
      ) {
        return false;
      }
      if (likeComp && isSparkling(picked) && countSparkling(selected) >= maxSparkling) {
        return false;
      }
      selected.push(picked);
      return true;
    }

    function fillUntil(limit, opts) {
      var guard = 0;
      while (selected.length < limit && guard < 200) {
        guard += 1;
        var rem = remainingPool();
        if (!rem.length) break;
        var progressed = false;
        for (var i = 0; i < rem.length; i++) {
          if (tryAdd(rem[i], opts)) {
            progressed = true;
            break;
          }
        }
        if (!progressed) break;
      }
    }

    // --- seek + red combined route ---
    if (seekRedRoute) {
      var tiers =
        seekRedRoute.sparklingColourTiers ||
        (seekRedRoute.allowedSparklingColours || ['red', 'rose']).map(function (c) {
          return [c];
        });
      var prioritySparkling = [];
      for (var ti = 0; ti < tiers.length; ti++) {
        var tierCols = tiers[ti];
        var tierWines = eligible
          .filter(function (w) {
            return isSparkling(w) && inColourList(w.colour, tierCols);
          })
          .sort(sortScored);
        if (tierWines.length) {
          prioritySparkling = tierWines.map(function (w) {
            var copy = Object.assign({}, w);
            copy.prioritySparklingTier = true;
            return copy;
          });
          break;
        }
      }
      var redStill = eligible
        .filter(function (w) {
          return !isSparkling(w) && inColourList(w.colour, seekRedRoute.allowedStillColours || ['red']);
        })
        .sort(sortScored);
      selected = prioritySparkling.concat(redStill).slice(0, maxCount);
    } else {
      // like / like+red / default fill
      var skipStyle = !!likeRedRoute && combined.overrideColourPreference;

      if (likeComp && minSparkling > 0) {
        var sparkPick = pickPreferredSparkling(remainingPool());
        if (sparkPick) tryAdd(sparkPick, { skipStyleGuard: skipStyle });
      }

      fillUntil(primaryCount, { skipStyleGuard: skipStyle });
      fillUntil(maxCount, { skipStyleGuard: skipStyle });

      if (likeComp && countSparkling(selected) < minSparkling) {
        var needSpark = pickPreferredSparkling(remainingPool());
        if (needSpark) {
          if (selected.length < maxCount) {
            tryAdd(needSpark, { skipStyleGuard: skipStyle });
          } else {
            var replaceStill = -1;
            for (var rsi = selected.length - 1; rsi >= 0; rsi--) {
              if (!isSparkling(selected[rsi])) {
                replaceStill = rsi;
                break;
              }
            }
            if (replaceStill >= 0) {
              selected.splice(replaceStill, 1);
              selected.push(needSpark);
              selected.sort(sortScored);
            }
          }
        }
      }

      if (likeComp && countStill(selected) < minStill) {
        var stillPool = remainingPool()
          .filter(function (w) {
            return !isSparkling(w);
          })
          .sort(sortScored);
        if (stillPool.length) {
          var still = stillPool[0];
          if (selected.length < maxCount) {
            tryAdd(still, { skipStyleGuard: skipStyle });
          } else {
            var replaceIdx = -1;
            for (var ri = selected.length - 1; ri >= 0; ri--) {
              if (isSparkling(selected[ri]) && countSparkling(selected) > minSparkling) {
                replaceIdx = ri;
                break;
              }
            }
            if (replaceIdx < 0) {
              for (var rj = selected.length - 1; rj >= 0; rj--) {
                if (isSparkling(selected[rj])) {
                  replaceIdx = rj;
                  break;
                }
              }
            }
            if (replaceIdx >= 0) {
              selected.splice(replaceIdx, 1);
              selected.push(still);
              selected.sort(sortScored);
            }
          }
        }
      }

      if (selected.length > maxCount) selected = selected.slice(0, maxCount);

      // like / like+red: final order by finalMatch (sparkling not forced first)
      if (likeComp) selected.sort(sortScored);
    }

    // Assign presentationScore then normalize displayMatch on the final ordered set
    for (var pi = 0; pi < selected.length; pi++) {
      selected[pi].presentationScore = presentationScoreForWine(selected[pi], config);
    }
    normalizeDisplayMatches(selected, config);

    var cards = selected.map(function (wine, index) {
      var displayMatch = wine.displayMatch;
      var presentationScore = wine.presentationScore;
      return {
        rank: index + 1,
        group: index < primaryCount ? 'best' : 'also',
        groupLabel:
          index < primaryCount
            ? qi('Лучшее совпадение', 'Eng yaxshi moslik')
            : qi('Также может понравиться', 'Shuningdek yoqishi mumkin'),
        wine: wine,
        finalMatch: wine.finalMatch,
        presentationScore: presentationScore,
        displayMatch: displayMatch,
        scoreLabel: formatScoreLabel(config, displayMatch)
      };
    });

    return { cards: cards, eligibleCount: eligible.length, eligible: eligible };
  }

  function formatScoreLabel(config, score) {
    var tpl =
      config.scoreLabel ||
      qi('Подходит на {displayMatch}%', 'Mos keladi {displayMatch}%');
    return tpl
      .replace('{displayMatch}', String(score))
      .replace('{score}', String(score));
  }

  function profileName(profileState) {
    var p = profileState.profile;
    var c = profileState.confidence;
    function ok(dim) {
      return (c[dim] || 0) >= 0.6;
    }
    var aroma = profileState.userAroma || {};
    var hasSpiceOrDark =
      (aroma.spice_oak || 0) >= 0.4 || (aroma.dark_fruit || 0) >= 0.4;

    if (ok('freshness') && p.freshness >= 65 && ok('body') && p.body <= 45) {
      return qi('Свежий и лёгкий', 'Yangi va yengil');
    }
    if (
      ok('freshness') &&
      p.freshness >= 60 &&
      ok('grip') &&
      p.grip >= 60 &&
      ok('sweetness') &&
      p.sweetness <= 35
    ) {
      return qi('Сухой и структурный', 'Quruq va tuzilmali');
    }
    if (ok('body') && p.body >= 65 && ok('grip') && p.grip <= 45) {
      return qi('Мягкий и округлый', 'Yumshoq va yumaloq');
    }
    if (
      ok('body') &&
      p.body >= 65 &&
      ok('grip') &&
      p.grip >= 55 &&
      hasSpiceOrDark
    ) {
      return qi('Насыщенный и пряный', 'Boy va ziravorli');
    }
    if (ok('sweetness') && p.sweetness >= 60 && ok('body') && p.body >= 50) {
      return qi('Фруктовый и мягкий', 'Mevali va yumshoq');
    }
    return qi('Сбалансированный', 'Muvozanatli');
  }

  function topDisplayScales(profileState, config) {
    var dims = config.dimensions || {};
    var list = DIMENSIONS.map(function (d) {
      return {
        id: d,
        displayName: (dims[d] && dims[d].displayName) || d,
        value: profileState.profile[d],
        confidence: profileState.confidence[d] || 0
      };
    });
    list.sort(function (a, b) {
      return b.confidence - a.confidence;
    });
    return list.slice(0, 3).map(function (item) {
      var level = qi('средняя', 'o‘rtacha');
      if (item.value >= 65) level = qi('высокая', 'yuqori');
      else if (item.value <= 35) level = qi('низкая', 'past');
      return {
        id: item.id,
        displayName: item.displayName,
        value: item.value,
        level: level,
        confidence: item.confidence
      };
    });
  }

  function buildExplanation(profileState, wine, config) {
    var everyday = profileState.contributingAnswers.filter(function (a) {
      return a.kind === 'everyday';
    });
    var direct = profileState.contributingAnswers.filter(function (a) {
      return a.kind === 'direct' && a.confidence > 0;
    });

    var picks = [];
    var seenQ = {};
    for (var i = 0; i < everyday.length && picks.length < 2; i++) {
      if (seenQ[everyday[i].questionId]) continue;
      seenQ[everyday[i].questionId] = 1;
      picks.push(everyday[i].label);
    }
    if (picks.length < 2) {
      for (var j = 0; j < direct.length && picks.length < 2; j++) {
        if (seenQ[direct[j].questionId]) continue;
        seenQ[direct[j].questionId] = 1;
        picks.push(direct[j].label);
      }
    }

    var wineTags = wine.aroma_tags || [];
    var topTags = topUserAromaTags(profileState.userAroma, config.aromaRanking || {});
    var matchedAroma = topTags.filter(function (t) {
      return wineTags.indexOf(t) !== -1;
    }).slice(0, 2);

    if (!picks.length) {
      return (
        wine.tasting_summary ||
        qi('Совпадение по вашему профилю вкуса.', 'Ta’m profilingizga mos keladi.')
      );
    }

    var line1 = qi('Вы выбрали ', 'Siz tanladingiz: ') + picks.join(', ') + '.';
    var parts = [];
    if ((profileState.confidence.freshness || 0) > 0.4 && wine.freshness_score >= 60) {
      parts.push(qi('свежее', 'yangi'));
    }
    if ((profileState.confidence.sweetness || 0) > 0 && wine.sweetness_score <= 35) {
      parts.push(qi('сухое', 'quruq'));
    }
    if (wine.colour === 'white') parts.push(qi('белое', 'oq'));
    else if (wine.colour === 'red') parts.push(qi('красное', 'qizil'));
    else if (wine.colour === 'rose') parts.push(qi('розовое', 'pushti'));

    var aromaHint = '';
    if (matchedAroma.length) {
      aromaHint = qi(' с характером ', ' — xususiyat: ') + matchedAroma.join(' / ');
    }

    var line2 =
      qi('Поэтому вам может подойти это ', 'Shuning uchun sizga ushbu ') +
      (parts.length ? parts.join(' ') + ' ' : '') +
      qi('вино', 'vino') +
      aromaHint +
      '.';

    return line1 + ' ' + line2;
  }

  /**
   * Full recommend pipeline.
   * @param {object} config
   * @param {Record<string,string|null>} answers
   * @param {object[]} catalogue
   */
  function recommend(config, answers, catalogue) {
    var diagnostics = [];
    var pipelineCounts = {
      catalogue: 0,
      afterValidation: 0,
      afterHardFilter: 0,
      afterScore: 0,
      afterMinMatch: 0,
      afterCombinedRoute: 0,
      finalCards: 0,
      stillRedAfterHardFilter: 0,
      route: null
    };
    var profileState = buildProfile(config, answers || {});
    var seekRed = isSeekRedCombined(profileState);
    var likeRed = isLikeRedCombined(profileState);
    pipelineCounts.route = seekRed ? 'seek_red' : likeRed ? 'like_red' : 'default';

    if (!profileState.readyForCards) {
      return {
        version: config.version,
        profileState: profileState,
        profileName: null,
        displayScales: [],
        showPercent: false,
        mode: 'need_more_answers',
        remainingMeaningfulNeeded: profileState.remainingMeaningfulNeeded,
        message:
          quizLocale() === 'uz'
            ? 'Yana ' + profileState.remainingMeaningfulNeeded + ' ta savolga javob bering'
            : 'Ответьте ещё на ' +
              profileState.remainingMeaningfulNeeded +
              ' ' +
              pluralQuestions(profileState.remainingMeaningfulNeeded),
        cards: [],
        diagnostics: diagnostics,
        pipelineCounts: pipelineCounts
      };
    }

    var topTags = topUserAromaTags(profileState.userAroma, config.aromaRanking || {});
    var scored = [];
    var minMatch = (config.hardFilters && config.hardFilters.minimumMatch) || 55;
    pipelineCounts.catalogue = (catalogue || []).length;

    for (var i = 0; i < (catalogue || []).length; i++) {
      var wine = normalizeWine(catalogue[i]);
      var validation = validateWine(wine, config);
      if (!validation.ok) {
        diagnostics.push({
          handle: wine.handle || null,
          stage: 'catalogue_validation',
          reasons: validation.reasons
        });
        continue;
      }
      pipelineCounts.afterValidation += 1;

      // seek+red: decide BEFORE general seek sparkling filter (inside hardFilterReasons).
      var hard = hardFilterReasons(wine, profileState, config);
      if (hard.length) {
        diagnostics.push({
          handle: wine.handle,
          stage: 'hard_filter',
          reasons: hard
        });
        continue;
      }
      pipelineCounts.afterHardFilter += 1;
      if (
        seekRed &&
        wine.colour === 'red' &&
        (wine.bubbles_score == null || wine.bubbles_score < 55)
      ) {
        pipelineCounts.stillRedAfterHardFilter += 1;
      }

      var scoredWine = scoreWine(wine, profileState, topTags, config);
      if (!scoredWine) {
        diagnostics.push({
          handle: wine.handle,
          stage: 'score',
          reasons: ['zero_confidence_denominator']
        });
        continue;
      }
      pipelineCounts.afterScore += 1;
      scored.push(scoredWine);
    }

    scored.sort(sortScored);
    pipelineCounts.afterMinMatch = scored.filter(function (s) {
      return s.finalMatch >= minMatch;
    }).length;

    var formed = formResults(scored, profileState, config);
    pipelineCounts.afterCombinedRoute = (formed.cards || []).length;
    pipelineCounts.finalCards = pipelineCounts.afterCombinedRoute;

    diagnostics.push({
      stage: 'pipeline_counts',
      counts: pipelineCounts,
      seekRed: seekRed,
      likeRed: likeRed,
      colourRoute: profileState.colourRoute,
      bubblesAnswer: profileState.directAnswerIds && profileState.directAnswerIds.bubbles
    });

    var cards = formed.cards.map(function (card) {
      return Object.assign({}, card, {
        explanation: buildExplanation(profileState, card.wine, config),
        showPercent: profileState.showPercent
      });
    });

    var mode = profileState.showPercent ? 'full' : 'preliminary';

    return {
      version: config.version,
      profileState: profileState,
      profileName: profileName(profileState),
      displayScales: topDisplayScales(profileState, config),
      showPercent: profileState.showPercent,
      mode: mode,
      message:
        mode === 'preliminary'
          ? qi(
              'Предварительная подборка — ответьте ещё на несколько вопросов, чтобы увидеть проценты.',
              'Dastlabki tanlov — foizlarni ko‘rish uchun yana bir nechta savolga javob bering.'
            )
          : null,
      cards: cards,
      diagnostics: diagnostics,
      pipelineCounts: pipelineCounts,
      topAromaTags: topTags
    };
  }

  function pluralQuestions(n) {
    var mod10 = n % 10;
    var mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'вопрос';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'вопроса';
    return 'вопросов';
  }

  /** Helpers for tests / UI */
  function createBaseWine(overrides) {
    return Object.assign(
      {
        handle: 'wine',
        title: 'Wine',
        published: true,
        availableForSale: true,
        recommendable: true,
        profile_version: 'v1.6',
        profile_status: 'approved',
        colour: 'white',
        bubbles_score: 0,
        freshness_score: 70,
        sweetness_score: 20,
        body_score: 40,
        grip_score: 20,
        aroma_tags: ['citrus_green'],
        style_family: 'crisp_white',
        tasting_summary: qi('Свежее сухое белое.', 'Yangi quruq oq.')
      },
      overrides || {}
    );
  }

  return {
    DIMENSIONS: DIMENSIONS,
    ALLOWED_COLOURS: ALLOWED_COLOURS,
    ALLOWED_STYLE_FAMILIES: ALLOWED_STYLE_FAMILIES,
    buildProfile: buildProfile,
    validateWine: validateWine,
    normalizeWine: normalizeWine,
    isSeekRedCombined: isSeekRedCombined,
    isLikeRedCombined: isLikeRedCombined,
    hardFilterReasons: hardFilterReasons,
    scoreWine: scoreWine,
    topUserAromaTags: topUserAromaTags,
    aromaBonusForWine: aromaBonusForWine,
    spiceOakAdjustment: spiceOakAdjustment,
    colourBonusForWine: colourBonusForWine,
    toDisplayMatch: toDisplayMatch,
    normalizeDisplayMatches: normalizeDisplayMatches,
    presentationScoreForWine: presentationScoreForWine,
    formResults: formResults,
    recommend: recommend,
    formatScoreLabel: formatScoreLabel,
    profileName: profileName,
    createBaseWine: createBaseWine,
    clamp: clamp
  };
});
